import React, { useCallback } from 'react'
import { useStore } from '../../store/store';
import AdderColumns from './AdderColumns';
import { ItemTypes, MASKS_TYPE, PRIMARY_DARK, containerBackground, contentHeight, defaultItemValues, pageBackground } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import { gridUnit } from '../../constants/gridConfig';
import { initializeBoxContent, isADrawBox, isATitleBox, reinitializeBoxWidthAndHeight } from '../../utils/boxes';
import { computeColumnWidth } from '../../utils/columns';
import { isInsideLinkedSection } from '../../utils/containers';
import { useDrop } from 'react-dnd';
import ContainerWrapper from './ContainerWrapper';
import ContainerTemplateGallery from '../common/ContainerTemplateGallery';
import { notification } from 'antd';

const accept = [
  'newBox',
  ItemTypes.CONTAINER_COPIED,
  ItemTypes.CONTAINER_TEMPLATE,
  ItemTypes.IMG,
  ItemTypes.TEXT,
  ItemTypes.SHAPE,
  ItemTypes.LINE,
  ItemTypes.TABLE,
  ItemTypes.TITLE,
  ItemTypes.TITLE_2,
  ItemTypes.TITLE_3,
  ItemTypes.TITLE_4,
  ItemTypes.SUMMARY,
  ItemTypes.MASK,
  ItemTypes.CHECKBOXS_VARIABLE,
  ItemTypes.SELECTOR_VARIABLE,
  ItemTypes.TEXT_VARIABLE,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TABLE_VARIABLE,
  ItemTypes.ORG_CHART
];

const selector = ({
  boxes,
  configuration,
  addContainers,
  addCopiedContainer,
  addContainerFromTemplate,
  containers,
  landscape,
  zoomLevel,
}) => ({
  boxes,
  containers,
  addContainers,
  addCopiedContainer,
  addContainerFromTemplate,
  configuration,
  landscape,
  zoomLevel,
  marginLeft: configuration.margins.left * gridUnit,
  marginRight: configuration.margins.right * gridUnit,
  titles: boxes.filter((box) => isATitleBox(box.type)),
  lineSpacing: configuration.lineSpacing,
});

const ItemWrapper = React.memo(({ index: windowItemsIndex, itemId, itemLength, type, titleCollapsed, previousItemId, isPage = false }) => {
  const {
    addContainers,
    addCopiedContainer,
    addContainerFromTemplate,
    configuration,
    marginLeft,
    marginRight,
    titles,
    containers,
    boxes,
    landscape,
    lineSpacing,
    zoomLevel,
  } = useStore(selector);
  const containerId = itemId
  const containerIndex = containers.findIndex((c) => c.id === containerId)
  const inside_linked_section = isInsideLinkedSection(containers, boxes, containerIndex)
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const unlockProposal = useStore(({ configuration }) => configuration.unlockProposal)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const isDocumentLoading = useStore(({ isDocumentLoading }) => isDocumentLoading)

  // const findBoxIdxByColumnId = useCallback((columnId) => {
  //   return boxes.findIndex((b) => b.columnId === columnId)
  // }, [boxes])

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (message) => {
    api.warning({
      message: 'Ajout impossible',
      description: message,
      placement: 'top',
    });
  };

  const onDrop = ({ box: element, monitor, isFromAdder, insertionIndex }) => {
    if (titleCollapsed) {
      openNotification(<div>Vous ne pouvez pas déposer d'élément dans un <b>titre non déplié</b></div>)
      return
    }
    if (inside_linked_section) {
      openNotification(<div>Vous ne pouvez pas déposer d'élément dans une <b>section liée</b></div>)
      return
    }
    // const container = containers.find((c) => c.id === itemId);
    // if (container) {
    //   const boxIdx = findBoxIdxByColumnId(container.columnsIds[0]);

    //   for (let i = boxIdx; i >= 0; i--) {
    //     const b = boxes[i];
    //     if (isATitleBox(b.type) && b.collapsed === true) {
    //       unCollapseTitles({ index: b.content.indexTitle[0] });
    //       scrollToItem(boxes[boxIdx], containers);
    //       break;
    //     }
    //   }
    // }

    // Disposition prête à l'emploi : tout est fait côté store, qui construit la
    // grille et son contenu en une passe (cf. addContainerFromTemplate). Rien à
    // préparer ici, l'item de glisser ne transporte qu'un identifiant.
    if (monitor.getItemType() === ItemTypes.CONTAINER_TEMPLATE) {
      addContainerFromTemplate({
        templateId: element.templateId,
        index: isFromAdder ? insertionIndex : undefined,
      });
      return;
    }

    if (monitor.getItemType() === ItemTypes.CONTAINER_COPIED) {
      let index;
      if (isFromAdder) {
        index = insertionIndex;
      }
      // Grille (colonnes ET lignes imbriquées) + box reposées d'un bloc, côté
      // store : ici on ne sait rien des lignes.
      addCopiedContainer({ index, copy: element })
    }

    if (monitor.getItemType() === 'newBox') {
      let newBox = initializeBoxContent({
        box: element,
        configuration,
        titles,
      });

      const drawMode = isADrawBox(element.type);

      if (!drawMode) {
        newBox = reinitializeBoxWidthAndHeight({
          box: newBox,
          columnWidth: computeColumnWidth({
            landscape,
            marginRight,
            marginLeft
          }),
          onCreate: true,
          header,
          footer
        })
      }
      let index;
      if (isFromAdder) {
        index = insertionIndex;
      }
      addContainers({
        index,
        drawMode,
        box: newBox,
      })
    }
  }

  const boxCanDrop = useCallback((_, monitor) => {
    const box = monitor.getItem();

    if (box.type === ItemTypes.GROUP_BOXES) return false
    if (landscape && box?.drawMode && box?.copiedContainer) return true;
    if (landscape && (!isADrawBox(box.type))) {
      return false;
    }
    // Can't move box from parent to another one
    if (box.parentId) {
      return false
    }
    if (!isTemplate && !unlockProposal) {
      return false
    }
    return true
  }, [unlockProposal, isTemplate, landscape])

  const [{ canDrop, boxIsOver }, dropAdder] = useDrop({
    accept,
    drop: (box, monitor) => onDrop({
      box,
      monitor,
      isFromAdder: true,
      insertionIndex: containers.length === 0 ? null : containers.length - 1
    }),
    canDrop: boxCanDrop,
    collect: (monitor) => ({
      boxIsOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    }),
  });

  const [, dropInfinite] = useDrop({
    accept,
    canDrop: boxCanDrop,
    drop: (box, monitor) => onDrop({ box, monitor, isFromAdder: false })
  });

  const addTextBoxOnDoubleClick = useCallback(
    () => {
      if (landscape || (!isTemplate && !unlockProposal)) return;
      let newBox = initializeBoxContent({
        box: {
          ...defaultItemValues.text,
          type: 'text',
        },
        configuration,
        titles,
      });
      newBox = reinitializeBoxWidthAndHeight({
        box: newBox,
        columnWidth: computeColumnWidth({
          landscape,
          marginRight,
          marginLeft
        }),
        onCreate: true,
        header,
        footer
      })
      addContainers({ box: newBox });
    },
    [landscape, isTemplate, unlockProposal, configuration, titles, marginRight, marginLeft, header, footer, addContainers]
  );

  // Galerie de démarrage : uniquement sur un document réellement vide, et là
  // où l'on peut éditer. En diapositive (paysage), le contenu est positionné
  // librement : une grille de colonnes n'y veut rien dire.
  //
  // `isDocumentLoading` est décisif : un document en cours de chargement a bien
  // zéro container, et sans cette garde la galerie s'affichait le temps du
  // chargement — d'autant plus longtemps que le document est gros. Le drapeau
  // part à `true` dès l'initialisation du store quand l'URL désigne un document
  // (cf. hasDocumentToLoad), donc la garde tient dès le tout premier rendu.
  const showEmptyGallery =
    !isDocumentLoading &&
    containers.length === 0 &&
    !landscape &&
    (isTemplate || unlockProposal);

  if (windowItemsIndex + 1 === itemLength) {
    return (
      <div data-role={pageBackground} data-parent={containerBackground}>
        <div
          ref={dropAdder}
          style={{
            marginTop: windowItemsIndex === 0 ? 35 : 0,
            position: 'relative'
          }}>
          <AdderColumns withoutContainers={itemLength === 1} boxIsOver={boxIsOver} canDrop={canDrop}
            containerIndex={containers.length === 0 ? null : containers.length - 1} />
        </div>
        <div
          id="infinite-page-drop"
          style={{
            width: "100%",
            height: contentHeight * (1 / (effectiveZoom(zoomLevel, landscape) / 100)),
            backgroundColor: "white",
            position: 'relative'
          }}
          data-role={pageBackground}
          data-parent={containerBackground}
          onDoubleClick={addTextBoxOnDoubleClick}
          ref={dropInfinite}
        >
          {/* Document vide : le moment où l'utilisateur a tout à décider, et
              où une page blanche ne propose rien. La galerie y remplace le
              vide — le premier geste pose une structure au lieu d'une box
              seule qu'il faudrait redécouper ensuite. */}
          {showEmptyGallery && (
            <div
              data-role={pageBackground}
              data-parent={containerBackground}
              // La page vide crée une box texte au double-clic : sans ça, un
              // double-clic un peu vif sur une vignette poserait la disposition
              // ET une box texte au-dessus.
              onDoubleClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 90,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(78%, 560px)',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: PRIMARY_DARK, marginBottom: 4 }}>
                Commencer par une disposition
              </div>
              <div style={{ fontSize: 11, color: 'rgba(11, 34, 57, 0.55)', marginBottom: 16 }}>
                Cliquez pour la poser, ou glissez-la où vous voulez.
              </div>
              <ContainerTemplateGallery
                columnsCount={4}
                previewHeight={44}
                onPick={(template) => addContainerFromTemplate({ templateId: template.id })}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!containers.some((c) => c.id === containerId)) return null

  return (
    <div data-role={pageBackground} data-parent={containerBackground}
      style={{
        // Une page pleine est encadrée par des bandes grises (cf. InfinitePage) :
        // ses espacements blancs la feraient paraître plus haute que la feuille.
        // La barre d'ajout, placée au-dessus du container, tombe dans la bande.
        paddingTop: windowItemsIndex === 0 && !isPage ? 35 : 0,
        paddingBottom: isPage ? 0 : lineSpacing
      }}>
      {contextHolder}
      <ContainerWrapper
        containerId={containerId}
        container={containers.find((c) => c.id === containerId)}
        type={type}
        titleCollapsed={titleCollapsed}
        windowItemsIndex={windowItemsIndex}
        onDrop={onDrop}
        boxCanDrop={boxCanDrop} />
    </div>
  )
})

export default ItemWrapper
